import React, {ReactElement} from 'react';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Label from '../../../../incase-domain/components/label/Label';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {ContentType, FormMethod, FormTarget, IElementForm} from '../../../../incase-domain/elements/form';
import {FORM_TITLE_MAX} from '../../../../incase-domain/validators/elements/constant';
import {formValidator} from '../../../../incase-domain/validators/elements/form';
import {getEmptyElement} from '../../../../service/element';
import AccordionItem from '../../../../ui/components/AccordionItem';
import {swapArrayIndices} from '../../../../utils/utils';
import {generateErrorMessages, getErrorClasses} from '../../../../utils/validation';
import SectionTitle from '../../../elements/common/SectionTitle';
import {TABS} from '../../form/part/tabs';
import DebouncedTextInput from '../common/debounced/DebouncedTextInput';
import DebouncedNumberInput from '../common/debounced/DebounceNumberInput';
import ElementShadow from '../common/elementShadow';
import FontSelect from '../common/fontSelect';
import HighlightedFormElementTyped from './componentTyped';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedFormElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedFormElementComponent(props: HighlightedFormElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IElementForm;
    const updateHighlightedElement = useSetHighlightedElement();

    const formMethods: FormMethod[] = [FormMethod.POST, FormMethod.PUT];
    const formTargets: FormTarget[] = ['_blank', '_self', '_parent', '_top', 'framename'];

    const numOfInputs = highlightedElement.fieldElements?.filter((el) => el.type === ELEMENT.TEXT_INPUT).length || 0;

    function swapItemOrderHandler(newIndex: number, oldIndex: number): void {
        const newList = swapArrayIndices(highlightedElement.fieldElements || [], newIndex, oldIndex);
        updateHighlightedElement.single('fieldElements', newList);
    }

    const addElement = (): void => {
        const newFormElements = [
            ...(highlightedElement.fieldElements || []),
            getEmptyElement(highlightedElement.slideId, ELEMENT.TEXT_INPUT),
        ];

        updateHighlightedElement.single('fieldElements', newFormElements);
    };

    const removeElement = (index: number): void => {
        if (numOfInputs === 1) return;
        const formElements = [...(highlightedElement.fieldElements || [])];
        formElements.splice(index, 1);
        updateHighlightedElement.single('fieldElements', formElements);
    };

    const childElementChangeHandler = (
        index: number
    ): {single: (field: string, value: unknown) => void; multiple: (fields: string[], values: unknown[]) => void} => {
        const elements = [...(highlightedElement.fieldElements || [])];
        let selectedElement = elements[index];

        return {
            single: (field: string, value: unknown) => {
                selectedElement = {...selectedElement, [field]: value};
                elements[index] = selectedElement;
                updateHighlightedElement.single('fieldElements', elements);
            },
            multiple: (fields: string[], values: unknown[]) => {
                elements[index] = fields.reduce((acc, field, i) => ({...acc, [field]: values[i]}), selectedElement);
                updateHighlightedElement.single('fieldElements', elements);
            },
        };
    };

    function mapToSelectOption(value: string): {label: string; value: string} {
        return {label: value, value};
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <div className="l-group--form">
                    <div className="l-group__item">
                        <div className="u-flex u-v-center u-h-between u-gap-2">
                            <Label>{t('designSidepanel.fontSize')}</Label>
                            <DebouncedNumberInput
                                value={highlightedElement.fontSize || 2}
                                onChange={(newValue: number) => updateHighlightedElement.single('fontSize', newValue)}
                                step={0.1}
                                min={0.1}
                            />
                        </div>
                        <div className="l-group__item">
                            <FontSelect
                                highlightedElement={highlightedElement}
                                highlightedElementChangeHandler={updateHighlightedElement}
                            />
                        </div>
                    </div>
                </div>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.formSettings')} />

                    <div className="u-flex u-flex-col u-gap-2">
                        <div>
                            <Label>{t('designSidepanel.method')}</Label>

                            <DropdownMenuCompact
                                width="full"
                                className="c-sidepanel__dropdown"
                                items={[
                                    formMethods.map((el) => ({
                                        label: el,
                                        onClick: () => updateHighlightedElement.single('method', el),
                                    })),
                                ]}
                            >
                                <Button
                                    fullWidth
                                    size="xs"
                                    trailing="solar:alt-arrow-down-linear"
                                    variant="soft"
                                    className="c-sidepanel__dropdown_button"
                                >
                                    {(highlightedElement.method && mapToSelectOption(highlightedElement.method)?.label) ||
                                        t('designSidepanel.methodPlaceholder')}
                                </Button>
                            </DropdownMenuCompact>
                        </div>

                        <div>
                            <Label>{t('designSidepanel.contentType')}</Label>

                            <DropdownMenuCompact
                                width="full"
                                className="c-sidepanel__dropdown"
                                items={[
                                    Object.values(ContentType).map((el) => ({
                                        label: el,
                                        onClick: () => updateHighlightedElement.single('contentType', el),
                                    })),
                                ]}
                            >
                                <Button
                                    fullWidth
                                    size="xs"
                                    trailing="solar:alt-arrow-down-linear"
                                    variant="soft"
                                    className="c-sidepanel__dropdown_button"
                                >
                                    {(highlightedElement.contentType && mapToSelectOption(highlightedElement.contentType)?.label) ||
                                        t('designSidepanel.contentTypePlaceholder')}
                                </Button>
                            </DropdownMenuCompact>
                        </div>

                        <div>
                            <Label>{t('designSidepanel.target')}</Label>
                            <DropdownMenuCompact
                                width="full"
                                className="c-sidepanel__dropdown"
                                items={[
                                    formTargets.map((el) => ({
                                        label: el,
                                        onClick: () => updateHighlightedElement.single('target', el),
                                    })),
                                ]}
                            >
                                <Button
                                    fullWidth
                                    size="xs"
                                    trailing="solar:alt-arrow-down-linear"
                                    variant="soft"
                                    className="c-sidepanel__dropdown_button"
                                >
                                    {(highlightedElement.target && mapToSelectOption(highlightedElement.target)?.label) ||
                                        t('presEditPage.editSidebar.elements.selectPlaceholder')}
                                </Button>
                            </DropdownMenuCompact>
                        </div>

                        <div>
                            <Label>{t('designSidepanel.submitUrl')}</Label>
                            <DebouncedTextInput
                                value={highlightedElement.submitUrl || ''}
                                onChange={(value) => updateHighlightedElement.single('submitUrl', value)}
                                className="c-sidepanel__input"
                                maxLength={FORM_TITLE_MAX}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <div className={`${getErrorClasses(errors, 'title')}`}>
                    <SectionTitle label={t('designSidepanel.title')} />
                    <DebouncedTextInput
                        value={highlightedElement.title}
                        onChange={(value) => updateHighlightedElement.single('title', value)}
                        validator={(val) => {
                            const validation = formValidator.shape.title.safeParse(val);
                            return validation.success;
                        }}
                        className="c-sidepanel__input"
                        maxLength={FORM_TITLE_MAX}
                    />
                </div>
            </div>

            <div className="l-group--form">
                <SectionTitle
                    label={t('designSidepanel.formFields')}
                    leading={
                        <Button
                            className="c-sequenceQuestionElementOrder__shuffleButton"
                            size="2xs"
                            variant="ghost"
                            color="primary"
                            onClick={addElement}
                            leading="ph:plus-bold"
                        >
                            {t('designSidepanel.new')}
                        </Button>
                    }
                />
                <div className={`${getErrorClasses(errors, 'fieldElements')}`}>
                    <DragDropContext
                        onDragEnd={(e) => {
                            if (typeof e.destination?.index !== 'undefined') swapItemOrderHandler(e.destination.index, e.source.index);
                        }}
                    >
                        <Droppable droppableId={`${highlightedElement.type}-${highlightedElement.elementIndex}`}>
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {highlightedElement.fieldElements
                                        ?.sort((a, b) => {
                                            if (a.type === 'BUTTON') return 1;
                                            if (b.type === 'BUTTON') return -1;
                                            return 0;
                                        })
                                        .map((element, index) => {
                                            return (
                                                <Draggable key={`${index}`} draggableId={`${index}`} index={index}>
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className="u-mb-1"
                                                            key={`highlighted-field_element-answer-${index}`}
                                                        >
                                                            <AccordionItem
                                                                header={
                                                                    <div
                                                                        {...provided.dragHandleProps}
                                                                        className="u-flex u-h-between u-v-center u-mr-2"
                                                                        style={{width: '100%'}}
                                                                    >
                                                                        <div>
                                                                            {element.type === 'TEXT_INPUT'
                                                                                ? element.type.charAt(0) +
                                                                                  element.type.split('_').join(' ').slice(1).toLowerCase()
                                                                                : element.type.charAt(0) +
                                                                                  element.type.slice(1).toLowerCase()}
                                                                        </div>
                                                                        {element.type === 'TEXT_INPUT' && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="xs"
                                                                                leading="solar:trash-bin-minimalistic-linear"
                                                                                square
                                                                                onClick={() => removeElement(index)}
                                                                                disabled={numOfInputs === 1}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                }
                                                            >
                                                                <div className="u-mb-2">
                                                                    <HighlightedFormElementTyped
                                                                        element={element}
                                                                        elementChangeHandler={childElementChangeHandler(index)}
                                                                    />
                                                                </div>
                                                            </AccordionItem>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}

                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {generateErrorMessages(errors, 'fieldElements')}
                </div>
            </div>
        </>
    );
}

export default HighlightedFormElementComponent;
